/**
 *  Nuclias Unity Cloud Log
 *  https://dlinko365.sharepoint.com/:x:/r/teams/NewHopePlatform/_layouts/15/Doc.aspx?sourcedoc=%7B6FB0E897-A232-4C80-9661-A62D63C4DD87%7D&file=Nuclias%20Unity%20Cloud%20Log.xlsx&action=default&mobileredirect=true
 **/

import React, { useState, useEffect, useMemo } from "react";

import useAuthScope from "hooks/useAuthScope";
import useDataSourceManager from "hooks/useDataSourceManager";
import useTableInstance from "hooks/useTableInstance";
import useZustandStore from "zustandStore";

import { v4 as uuidv4 } from "uuid";
import { createColumnHelper } from "@tanstack/react-table";

import { Divider, Grid, Box, CircularProgress } from "@mui/material";

import MainCard from "components/cards/MainCard";
import { SolidButton as SolidRectangleButton } from "components/buttons/SolidRectangleButton";
import TabsField from "components/Tab";
import SearchInput from "components/SearchInput";
import ColumnFilterToggle from "components/Table/ColumnFilterToggle";
import ColumnVisibilityToggle from "components/Table/ColumnVisibilityToggle";
import { SolidButton } from "components/buttons/SolidButton";
import TableField from "components/Table";
import DrawerField from "components/Drawer";

import allEventsColumns from "./AllEvents/allEventsColumns";
import accessPointColumns from "./AccessPoint/accessPointColumns";
import switchColumns from "./Switch/switchColumns";
import routerColumns from "./Router/routerColumns";
import systemAuditColumns from "./SystemAudit/systemAuditColumns";

import { ReactComponent as ExportIcon } from "assets/images/icons/icn_btn_export.svg";
import icon_refresh from "assets/images/icons/icn_refresh.svg";

import { getTimestampByDays } from "utils/timeUtils";
import { exportTableDataToCSV } from "utils/export";
import { EVENTS_LOG_EVENTS_TYPE } from "const/eventsLog";

const VISIBILITY_GROUP_DATA = [
  {
    groupNames: ["Status"],
    columns: {
      "Status": [
        "severity", "createdAt", "siteName",
        "eventType", "deviceType", "deviceName",
        "deviceMacAddress", "description",
        "clientName", "clientMacAddress"
      ]
    }
  },
]

const modifyEventTypeColumn = (cols) => {
  return cols.map(col => {
    if (col.id === "eventType" || col.accessorKey === "eventType") {
      return {
        ...col,
        accessorFn: (row) => {
          const rawVal = row.eventType;
          return EVENTS_LOG_EVENTS_TYPE[rawVal] ?? rawVal ?? "-";
        },
        cell: info => <Box>{info.getValue()}</Box>,
        meta: {
          ...col.meta,
          exportValue: ({ row }) => EVENTS_LOG_EVENTS_TYPE[row.original.eventType] ?? row.original.eventType ?? "-"
        }
      };
    }
    return col;
  });
};

const Events = () => {
  const { auth, standalone, isAuthReady } = useAuthScope();
  const serviceStatus = useZustandStore((state) => state.serviceStatus);
  const isUnityProSubscribed = useZustandStore((state) => state.isUnityProSubscribed);

  const isSiteLevel = auth.scopeLevel === "site";
  const isLicenseReady = isSiteLevel ? serviceStatus !== null : true;

  const [eventsUuid, _setEventsUuid] = useState(uuidv4());
  const [updateEventsUuid, _setUpdateEventsUuid] = useState(uuidv4());

  const { dataMap, getData, sendRequest, sendBatch } = useDataSourceManager();

  const columnHelper = createColumnHelper();

  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerData, setDrawerData] = useState({});

  const [settingsData, setSettingsData] = useState(null);
  const [licenseDataLoaded, setLicenseDataLoaded] = useState(false);
  const settingsUuid = useMemo(() => uuidv4(), []);

  const [tabCountAll, setTabCountAll] = useState(0);
  const [tabCountAp, setTabCountAp] = useState(0);
  const [tabCountSw, setTabCountSw] = useState(0);
  const [tabCountRouter, setTabCountRouter] = useState(0);
  const [tabVal, setTabVal] = useState("all");

  const [tableDataAll, setTableDataAll] = useState([]);
  const [tableDataAp, setTableDataAp] = useState([]);
  const [tableDataSw, setTableDataSw] = useState([]);
  const [tableDataRouter, setTableDataRouter] = useState([]);
  const [tableDataSystemAudit, setTableDataSystemAudit] = useState([]);

  const allTableInstance = useTableInstance({
    tableName: "events",
    data: tableDataAll,
    columns: modifyEventTypeColumn(allEventsColumns(columnHelper, auth.permissions, setDrawerOpen, setDrawerData)),
    enableSorting: true,
    enablePagination: true,
    enableGlobalFilter: true,
    globalFilterMode: "column",
    globalFilterColumns: ["clientName", "clientMacAddress", "description"],
    enableRowSelection: true,
    hiddenColumns: ["clientName", "clientMacAddress"],
  });

  const apTableInstance = useTableInstance({
    tableName: "events",
    data: tableDataAp,
    columns: modifyEventTypeColumn(accessPointColumns(columnHelper, auth.permissions, setDrawerOpen, setDrawerData)),
    enableSorting: true,
    enablePagination: true,
    enableGlobalFilter: true,
    globalFilterMode: "column",
    globalFilterColumns: ["clientName", "clientMacAddress", "description"],
    enableRowSelection: true,
    hiddenColumns: ["clientName", "clientMacAddress"],
  });

  const swTableInstance = useTableInstance({
    tableName: "events",
    data: tableDataSw,
    columns: modifyEventTypeColumn(switchColumns(columnHelper, auth.permissions, setDrawerOpen, setDrawerData)),
    enableSorting: true,
    enablePagination: true,
    enableGlobalFilter: true,
    globalFilterMode: "column",
    globalFilterColumns: ["clientName", "clientMacAddress", "description"],
    enableRowSelection: true,
    hiddenColumns: ["clientName", "clientMacAddress"],
  });

  const routerTableInstance = useTableInstance({
    tableName: "events",
    data: tableDataRouter,
    columns: modifyEventTypeColumn(routerColumns(columnHelper, auth.permissions, setDrawerOpen, setDrawerData)),
    enableSorting: true,
    enablePagination: true,
    enableGlobalFilter: true,
    globalFilterMode: "column",
    globalFilterColumns: ["clientName", "clientMacAddress", "description"],
    enableRowSelection: true,
    hiddenColumns: ["clientName", "clientMacAddress"],
  });

  const systemAuditTableInstance = useTableInstance({
    tableName: "events",
    data: tableDataSystemAudit,
    columns: modifyEventTypeColumn(systemAuditColumns(columnHelper, auth.permissions, setDrawerOpen, setDrawerData)),
    enableSorting: true,
    enablePagination: true,
    enableGlobalFilter: true,
    globalFilterMode: "column",
    globalFilterColumns: ["clientName", "clientMacAddress", "description"],
    enableRowSelection: true,
    hiddenColumns: ["clientName", "clientMacAddress"],
  });

  const currentTableInstance = useMemo(() => {
    switch (tabVal) {
      case "all":
        return allTableInstance;
      case "access_points":
        return apTableInstance;
      case "switchs":
        return swTableInstance;
      case "routers":
        return routerTableInstance;
      case "system_audit":
        return systemAuditTableInstance;
      default:
        return null;
    }
  }, [tabVal, allTableInstance, apTableInstance, swTableInstance, routerTableInstance, systemAuditTableInstance]);

  const currentTableSelected = useMemo(() =>
    currentTableInstance && currentTableInstance.table.getSelectedRowModel().rows,
    [currentTableInstance]
  );

  function fetchReadEventsData() {
    setLoading(true);
    const baseUrl = standalone ? `/orgs/:orgId` : `/ents/:entId/orgs/:orgId`;

    const apiConfig = {
      org: `${baseUrl}/events.read`,
      site: `${baseUrl}/sites/:siteId/events.read`
    };

    const restfulParamsConfig = {
      standalone: {
        orgId: auth.realm?.id,
        siteId: auth.sites?.id,
      },
      nonStandalone: {
        entId: auth.realm?.id,
        orgId: auth.orgs?.id,
        siteId: auth.sites?.id,
      },
    };

    const apiKey = apiConfig[auth.scopeLevel];
    const mode = standalone ? "standalone" : "nonStandalone";
    const restfulParams = restfulParamsConfig[mode];

    const to = Date.now();
    const days = isSiteLevel && isUnityProSubscribed ? -180 : -7;
    const from = getTimestampByDays(days, to);

    sendRequest(
      apiKey,
      eventsUuid,
      restfulParams,
      {
        "filter": {
          "from": from,
          "to": to
        }
      }
    );
  }

  function handleAcknowledgeOnClick() {
    const baseUrl = standalone ? `/orgs/:orgId` : `/ents/:entId/orgs/:orgId`;

    const apiConfig = {
      org: `${baseUrl}/events/acknowledge.update`,
      site: `${baseUrl}/sites/:siteId/events/acknowledge.update`
    };

    const restfulParamsConfig = {
      standalone: {
        orgId: auth.realm?.id,
        siteId: auth.sites?.id,
      },
      nonStandalone: {
        entId: auth.realm?.id,
        orgId: auth.orgs?.id,
        siteId: auth.sites?.id,
      },
    };

    const apiKey = apiConfig[auth.scopeLevel];
    const mode = standalone ? "standalone" : "nonStandalone";
    const restfulParams = restfulParamsConfig[mode]
    const requestParams = {
      data: {
        "eventIds": currentTableSelected.map((item) => item.original.id)
      }
    }

    sendRequest(apiKey, updateEventsUuid, restfulParams, requestParams);
  }

  const fetchLicenseData = async () => {
    try {
      const realmId = auth.realm?.id || "";
      const orgId = auth.orgs?.id || "";

      const settingsApiKey = standalone
        ? "/orgs/:orgId/settings/general.read"
        : "/ents/:entId/orgs/:orgId/settings/general.read";

      const restfulParams = standalone
        ? { orgId: realmId }
        : { entId: realmId, orgId };

      const { getSendBatchData } = await sendBatch([
        {
          apiKey: settingsApiKey,
          requestUuid: settingsUuid,
          restfulPlaceholders: restfulParams,
          params: {},
        },
      ]);

      const settingsRes = getSendBatchData({ requestUuid: settingsUuid });

      if (settingsRes?.data) {
        setSettingsData(settingsRes.data);
      }
    } catch (error) {
      console.error("[Events] Error fetching license data via sendBatch:", error);
    } finally {
      setLicenseDataLoaded(true);
    }
  };

  useEffect(() => {
    if (isAuthReady && isLicenseReady) {
      fetchReadEventsData();
    }
  }, [isAuthReady, isLicenseReady, tabVal, isUnityProSubscribed, isSiteLevel]);

  useEffect(() => {
    const readResponse = getData(eventsUuid);

    if (readResponse && readResponse.data) {
      const allData = readResponse.data;
      const apData = allData.filter((item) => item.deviceType === 'ap');
      const swData = allData.filter((item) => item.deviceType === 'switch' || item.deviceType === 'nc');
      const routerData = allData.filter((item) => item.deviceType === 'router');
      const systemAuditData = allData.filter((item) =>
        item.eventType === 'SYSTEM_AUDIT' ||
        ['PROXY_SSH_AUTH_FAILED', 'PROXY_SSH_TIMEOUT', 'PROXY_SSH_REJECT', 'PROXY_SSH_FAILED'].includes(item.eventType)
      );

      const countAll = allData.length;
      const countAp = apData.length;
      const countSw = swData.length;
      const countRouter = routerData.length;

      setTabCountAll(countAll);
      setTabCountAp(countAp);
      setTabCountSw(countSw);
      setTabCountRouter(countRouter);

      setTableDataAll(allData);
      setTableDataAp(apData);
      setTableDataSw(swData);
      setTableDataRouter(routerData);
      setTableDataSystemAudit(systemAuditData);
      setLoading(false)

      if (!licenseDataLoaded) {
        fetchLicenseData();
      }
    }

    const updateResponse = getData(updateEventsUuid);
    if (updateResponse && updateResponse.data) {
      const tableMap = {
        all: {
          data: tableDataAll,
          setter: setTableDataAll,
        },
        access_points: {
          data: tableDataAp,
          setter: setTableDataAp,
        },
        switchs: {
          data: tableDataSw,
          setter: setTableDataSw,
        },
        routers: {
          data: tableDataRouter,
          setter: setTableDataRouter,
        },
        system_audit: {
          data: tableDataSystemAudit,
          setter: setTableDataSystemAudit,
        },
      };

      const updateData = updateResponse.data;

      const successEventIds = updateData
        .filter(item => item.status === "success")
        .map(item => item.eventId);

      const applyAcknowledge = (data, successEventIds) =>
        data.map(item =>
          successEventIds.includes(item.id)
            ? { ...item, acknowledge: true }
            : item
        );

      const currentTable = tableMap[tabVal];
      if (!currentTable) return;

      currentTable.setter(applyAcknowledge(currentTable.data, successEventIds));
      currentTableInstance.table.resetRowSelection();
    }
  }, [dataMap])

  return (
    <>
      <MainCard>
        <Grid container sx={{ justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {currentTableInstance?.table?.getSelectedRowModel()?.rows?.length > 0 && (
              <>
                <SolidRectangleButton onClick={handleAcknowledgeOnClick}>
                  Acknowledge
                </SolidRectangleButton>
                <Divider
                  sx={{ width: "1px", height: "40px", margin: "20px 20px" }}
                  orientation="vertical"
                  flexItem
                />
              </>
            )}
            <TabsField
              tabVal={tabVal}
              handleTabsChange={(_event, newValue) => setTabVal(newValue)}
              tabData={[
                {
                  name: "All",
                  value: "all",
                  disabled: null,
                  tabCount: tabCountAll,
                },
                {
                  name: "Access Points",
                  value: "access_points",
                  disabled: null,
                  tabCount: tabCountAp,
                },
                {
                  name: "Switches",
                  value: "switchs",
                  disabled: null,
                  tabCount: tabCountSw,
                },
                {
                  name: "Routers",
                  value: "routers",
                  disabled: null,
                  tabCount: tabCountRouter,
                },
                {
                  name: "System Audit",
                  value: "system_audit",
                  disabled: null,
                },
              ]}
            />
          </Box>
          <Box>
            <Grid container sx={{ alignItems: "center" }}>
              <Divider
                sx={{ width: "1px", height: "40px", margin: "20px 20px" }}
                orientation="vertical"
                flexItem
              />
              <Box
                sx={{ display: "flex", padding: "3px 10px 3px", marginRight: "5px" }}>
                <SearchInput
                  placeholder="Search by Name, MAC, Description"
                  value={currentTableInstance.globalFilter}
                  onChange={currentTableInstance.setGlobalFilter}
                />
              </Box>
              <ColumnFilterToggle
                {...currentTableInstance}
                filterData={["severity", "siteName", "eventType", "deviceType"]}
              />
              <ColumnVisibilityToggle
                maxWidth={360}
                {...currentTableInstance}
                groupedData={VISIBILITY_GROUP_DATA}
                hiddenCheck={["severity", "createdAt", "siteName",
                  "eventType", "deviceType", "deviceName"]}
              />
              <Box
                margin="10px 10px 0px 8px"
                sx={{ cursor: "pointer" }}
                onClick={fetchReadEventsData}
              >
                <img src={icon_refresh} alt="" />
              </Box>
              <SolidButton
                startIcon={<ExportIcon />}
                width="100px"
                height="32px"
                onClick={() => exportTableDataToCSV(currentTableInstance)}
              >
                Export
              </SolidButton>
            </Grid>
          </Box>
        </Grid>
        {currentTableInstance && (
          <TableField
            {...currentTableInstance}
            overlayData={["acknowledge"]}
            pendingChildren={
              loading === true && (
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <CircularProgress />
                </Box>
              )
            }
          />
        )}
      </MainCard>
      {
        drawerOpen && (drawerData.key === "DeviceNameWithMACAsLink" || drawerData.key === "PayloadDeviceNameWithMACAsLink") && (
          <DrawerField
            contentKey={drawerData.deviceId}
            open={drawerOpen}
            width="auto"
            handleClose={() => setDrawerOpen(false)}
            commonContent="devices"
            contentProps={{
              fetchFlag: true,
              auth,
              deviceId: ((drawerData.key === "DeviceNameWithMACAsLink" && drawerData.deviceId) ||
                (drawerData.key === "PayloadDeviceNameWithMACAsLink" && drawerData.payload.deviceId)),
              settingsData,
            }}
          />
        )
      }
      {
        drawerOpen && (drawerData.key === "AccountNameWithUniqueLink" || drawerData.key === "ChangedAccountNameWithUniqueLink") && (
          <DrawerField
            contentKey={`users_${uuidv4()}`}
            open={drawerOpen}
            width="550px"
            handleClose={() => setDrawerOpen(false)}
            commonContent="users"
            contentProps={{
              userId: ((drawerData.key === "AccountNameWithUniqueLink" && drawerData.accountId) ||
                (drawerData.key === "ChangedAccountNameWithUniqueLink" && drawerData.payload.changedAccountId)),
            }}
          />
        )
      }
      {
        drawerOpen && (drawerData.key === "ClientNameWithMACAsLink") && (
          <DrawerField
            contentKey={`clients_${uuidv4()}`}
            open={drawerOpen}
            width="550px"
            handleClose={() => setDrawerOpen(false)}
            commonContent="clients"
            contentProps={{
              auth,
              selectedSiteId: drawerData.siteId,
              selectedClientId: drawerData.payload.macAddress,
            }}
          />
        )
      }
    </>
  );
};

export default Events;
